// P20d — the case-ref GRAMMAR, as a pattern rather than as code.
//
// WHAT MOVED: the shape of a case reference was written into three places —
// cases.js / collectiveCases.js built `${year}/${String(n).padStart(3,'0')}`
// and scanned for the `${year}/` prefix, and caseRefParser.js matched a
// hardcoded /(\d{4})(?:\/(\d{1,4})|WAT)/. P17.4 classified YYYY/NNN as PRESET
// CONTENT and retained the YYYYWAT token as "parser-only backward-compat,
// revisited only if case-ref patterns are lifted." This is that lift, so the
// condition fires: the generation pattern is FirmSettings.caseRefPattern
// (default "{YYYY}/{NNN}" — today's, exactly), and the parser accepts that
// pattern PLUS FirmSettings.caseRefLegacyPatterns, seeded ["{YYYY}WAT"] for
// tenant 1 and empty for everyone else.
//
// THE PATTERN LANGUAGE, in full:
//   {YYYY}  the 4-digit year
//   {N…}    the sequence. The COUNT of N's is the zero-padding width on
//           generation ({NNN} → 001). On PARSING a sequence token accepts
//           1..max(4, width) digits — the 1..4 window is today's literal
//           behaviour ("2024/1" and "2024/1234" both parse against
//           "{YYYY}/{NNN}"), widened only if a firm asks for a longer pad.
//   anything else is a literal.
// A pattern with no sequence token is parse-only (it can match a ref but
// cannot allocate one) — which is exactly what YYYYWAT is.
//
// BOUNDARIES ARE PART OF THE GRAMMAR, not of the caller. A ref must be flanked
// by a non-alphanumeric or by the string edge, and the year must be 2000-2099
// — both frozen from the old regex, which existed precisely so "12024/0001",
// "2024/0001234" and "X2025WATY" do NOT match a meeting subject.
//
// PURE: no prisma, no I/O, no DOM. server/src/lib/caseRefSettings.js is what
// reads the columns.
//
// WHY IT LIVES IN src/lib/*.mjs. Both ends of ref allocation now need this
// grammar: the server ALLOCATES from it, and the case / collective-case modals
// PRE-FILL from it (App.tsx openModal, CollectiveCaseView genRef) — and the
// prefill is usually what becomes the ref, since both create routes read
// `ref || generateRefTx(…)`. Two copies of a grammar is the F1/F2 "silently
// drift" disease the CDD collapse was written to cure, so this follows the
// same shape that cure established: ONE pure .mjs, imported by the client and
// `require()`d by the server (Node ≥20.17 requires ESM natively). It owns no
// side of the boundary; neither side reaches into the other.

const TOKEN_RE = /\{(YYYY|N+)\}/g;
export const YEAR_MIN = 2000;
export const YEAR_MAX = 2099;
// The sequence-width window a token accepts when parsing. Frozen from the old
// /(\d{1,4})/; a wider pad widens it, a narrower one never narrows it.
const SEQ_MIN_DIGITS = 1;
const SEQ_MAX_DIGITS = 4;

function escapeLiteral(s) {
  return s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
}

/** Split a pattern into its parts: {kind:'year'|'seq'|'literal', …}. Throws on
 *  a malformed pattern so a bad settings value fails loudly at the write, not
 *  silently at the next case creation. */
export function parsePattern(pattern) {
  const src = String(pattern == null ? '' : pattern);
  if (!src) throw new Error('case-ref pattern is empty');
  const parts = [];
  let last = 0;
  let m;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) parts.push({ kind: 'literal', text: src.slice(last, m.index) });
    parts.push(m[1] === 'YYYY' ? { kind: 'year' } : { kind: 'seq', width: m[1].length });
    last = m.index + m[0].length;
  }
  if (last < src.length) parts.push({ kind: 'literal', text: src.slice(last) });
  if (parts.some((p) => p.kind === 'literal' && /[{}]/.test(p.text)))
    throw new Error(`case-ref pattern has an unrecognised token: ${src}`);
  if (parts.filter((p) => p.kind === 'year').length > 1)
    throw new Error(`case-ref pattern has more than one {YYYY}: ${src}`);
  if (parts.filter((p) => p.kind === 'seq').length > 1)
    throw new Error(`case-ref pattern has more than one sequence token: ${src}`);
  return parts;
}

/** True when the pattern can ALLOCATE a ref (it has a sequence token). */
export function canGenerate(pattern) {
  return parsePattern(pattern).some((p) => p.kind === 'seq');
}

/** The ref this pattern produces for (year, seq). "{YYYY}/{NNN}" + (2026, 7)
 *  → "2026/007". A sequence longer than the pad is NOT truncated — padStart's
 *  behaviour, so the 1000th case of a year still gets a ref. */
export function formatRef(pattern, year, seq) {
  return parsePattern(pattern)
    .map((p) => {
      if (p.kind === 'literal') return p.text;
      if (p.kind === 'year') return String(year);
      return String(seq).padStart(p.width, '0');
    })
    .join('');
}

/** The literal prefix a year's refs all start with — what the "find the last
 *  ref of this year" scan does a startsWith() on. "{YYYY}/{NNN}" + 2026 →
 *  "2026/". Empty when the pattern starts with its sequence (the scan then
 *  falls back to matching every ref, which is correct, just less selective). */
export function prefixFor(pattern, year) {
  const parts = parsePattern(pattern);
  let out = '';
  for (const p of parts) {
    if (p.kind === 'seq') break;
    out += p.kind === 'year' ? String(year) : p.text;
  }
  return out;
}

/** The sequence number stored in `ref`, per this pattern, or null when `ref`
 *  does not fit the pattern at all.
 *
 *  This is the read-back half of allocation ("what number did last year's last
 *  ref carry?"), and it is deliberately WHOLE-STRING anchored: the frozen
 *  /\/(\d+)$/ it replaces refused to read a number out of "2026/abc", and a
 *  looser rule here would silently start the next year at the wrong number.
 *  The digit run is unbounded (as the frozen one was) — that asymmetry with
 *  the parser's 1..4 window is today's, kept. */
export function sequenceFrom(pattern, ref) {
  if (!ref || typeof ref !== 'string') return null;
  const parts = parsePattern(pattern);
  let body = '';
  let seqGroup = 0;
  let n = 0;
  for (const p of parts) {
    if (p.kind === 'literal') { body += escapeLiteral(p.text); continue; }
    if (p.kind === 'year') { body += '\\d{4}'; continue; }
    body += '(\\d+)';
    seqGroup = ++n;
  }
  if (!seqGroup) return null;
  const m = ref.match(new RegExp(`^${body}$`));
  return m ? parseInt(m[seqGroup], 10) : null;
}

/** The ref a CREATE MODAL should pre-fill, given the rows it already has.
 *
 *  This is the client-side twin of the server's generateRefTx, and it is here
 *  so the two cannot be written twice: the modal scans the rows it holds for
 *  this year's prefix, takes the highest sequence it can read, and formats the
 *  next one. It is a SUGGESTION — the server re-derives authoritatively under
 *  its advisory lock when the field is left empty, and the create route accepts
 *  whatever the user finally typed.
 *
 *  Frozen from the two hardcoded prefills it replaces (App.tsx openModal,
 *  CollectiveCaseView genRef): highest-wins (not last-wins), a ref whose
 *  sequence cannot be read is skipped rather than treated as zero, and an
 *  empty year starts at 1.
 */
export function nextRefFrom(pattern, rows, year = new Date().getFullYear()) {
  if (!pattern) return '';
  let prefix;
  try {
    prefix = prefixFor(pattern, year);
  } catch {
    return '';
  }
  let max = 0;
  for (const r of rows || []) {
    const ref = r && r.ref;
    if (!ref || !ref.startsWith(prefix)) continue;
    const n = sequenceFrom(pattern, ref);
    if (n != null && n > max) max = n;
  }
  return formatRef(pattern, year, max + 1);
}

/** Build the anchored matcher for one pattern. Group 1 is always the WHOLE
 *  ref — which is what makes both "where did it start" and "what did it say"
 *  fall out of the match instead of being reconstructed. The year and sequence
 *  groups follow it.
 *
 *  `kind` is the caller's LABEL for what this pattern allocates — 'case' or
 *  'cc'. It is carried, never inferred: this module has no idea what a
 *  collective case is, and a caller that does not label its patterns gets no
 *  label back. */
export function compile(pattern, kind = null) {
  const parts = parsePattern(pattern);
  let body = '';
  const groups = { ref: 1, year: 0, seq: 0 };
  let n = 1; // group 1 is the whole ref
  for (const p of parts) {
    if (p.kind === 'literal') { body += escapeLiteral(p.text); continue; }
    if (p.kind === 'year') { body += '(\\d{4})'; groups.year = ++n; continue; }
    const max = Math.max(SEQ_MAX_DIGITS, p.width);
    body += `(\\d{${SEQ_MIN_DIGITS},${max}})`;
    groups.seq = ++n;
  }
  // The frozen boundary rule: not inside a longer alphanumeric run, either side.
  return { pattern, kind, re: new RegExp(`(?:^|[^A-Za-z0-9])(${body})(?=$|[^A-Za-z0-9])`), groups };
}

/** Compile a list of patterns once, for a request that will parse many
 *  subjects. Takes bare pattern strings, or the LABELLED form
 *  `{ pattern, kind }` that the subject parser uses to say which ENTITY each
 *  grammar belongs to. Unusable patterns are skipped rather than throwing: a
 *  firm's bad legacy entry must not take the whole import down. */
export function compileAll(patterns) {
  const out = [];
  for (const p of patterns || []) {
    if (!p) continue;
    const pattern = typeof p === 'string' ? p : p.pattern;
    const kind = typeof p === 'string' ? null : (p.kind || null);
    if (!pattern) continue;
    try { out.push(compile(pattern, kind)); } catch { /* skip an unusable pattern */ }
  }
  return out;
}

/** Find the FIRST ref in `subject`. Returns the canonical
 *  { kind?, year, sequence|null, ref } the route matches against the ref
 *  column of the entity `kind` names, with the sequence's ORIGINAL width
 *  preserved: the DB stores what the user typed at creation, so canonicalising
 *  here would break the exact-match lookup.
 *
 *  `kind` is present only when the WINNING matcher was labelled (see compile).
 *  An unlabelled pattern list — a retune experiment, a test — parses exactly as
 *  it did before this key existed, key for key. */
export function matchRef(subject, compiled) {
  if (!subject || typeof subject !== 'string') return null;
  // FIRST MATCH WINS, and the range checks are applied AFTER that choice —
  // both frozen from the single alternating regex this replaces. That order is
  // load-bearing: "1999/001 and 2024/001" parsed to NOTHING before (the first
  // hit was out of the year window and the old parser returned null rather
  // than scanning on), and it must keep parsing to nothing.
  //
  // PRECEDENCE, in full — one rule per tie the corpus actually produces:
  //   1. EARLIEST START POSITION wins. Frozen. It is also what makes the
  //      collective form beat the case form for the default patterns without
  //      any special case: in "CC-2026/001" the CC pattern matches at 0 while
  //      the case pattern matches the FRAGMENT "2026/001" at 3, because the
  //      "CC-" reads to it as ordinary leading boundary text.
  //   2. Same position → the LONGER ref wins. When one pattern's match starts
  //      where another's does and contains it, the shorter one is reading a
  //      FRAGMENT of the longer ref, and a fragment is not a ref. This is what
  //      carries rule 1's guarantee to a firm whose collective pattern is a
  //      SUFFIX form ("{YYYY}/{NNN}-CC"), where both start at the same place.
  //   3. Same position AND same length → the order the patterns were given.
  //      This fires only when two patterns match the identical text, i.e. the
  //      firm has configured two kinds to the same grammar; nothing in the
  //      subject can tell them apart, so this is the tiebreak of record rather
  //      than a judgement. caseRefSettings.refMatchers() gives cc first.
  let best = null;
  for (const c of compiled) {
    const m = subject.match(c.re);
    if (!m) continue;
    // Group 1 is the ref itself; m[0] additionally holds the boundary
    // character when one was consumed, so this is where the ref starts.
    const len = m[c.groups.ref].length;
    const at = m.index + (m[0].length - len);
    if (!best || at < best.at || (at === best.at && len > best.len)) best = { at, len, m, c };
  }
  if (!best) return null;
  const { m, c } = best;
  const year = c.groups.year ? parseInt(m[c.groups.year], 10) : null;
  if (year != null && (year < YEAR_MIN || year > YEAR_MAX)) return null;
  const seqRaw = c.groups.seq ? m[c.groups.seq] : null;
  const sequence = seqRaw == null ? null : parseInt(seqRaw, 10);
  if (sequence != null && sequence < 1) return null;
  // The ref is re-emitted EXACTLY as it appeared (original sequence width, no
  // zero-padding): the DB stores what the user typed at creation, and the
  // caller does an exact-match lookup against that entity's ref column.
  const out = c.kind ? { kind: c.kind } : {};
  out.year = year;
  out.sequence = sequence;
  out.ref = m[c.groups.ref];
  return out;
}

